import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ShoppingCart, Heart } from 'lucide-react';

const CakeCollection = ({
  addToCart,
  showAboutCard = false,
  limit = null,
  selectedCategory = 'all',
  onCategoriesChange
}) => {
  const [cakes, setCakes] = useState([]);
  const [notification, setNotification] = useState(null);
  const [isAddingToCart, setIsAddingToCart] = useState(false);

  useEffect(() => {
    const fetchCakes = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/products`);

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const contentType = response.headers.get('content-type');
        if (!contentType || !contentType.includes('application/json')) {
          throw new SyntaxError(`Expected JSON but received ${contentType || 'unknown content type'}`);
        }

        const data = await response.json();

        if (!Array.isArray(data)) {
          throw new SyntaxError('Expected an array of products but received a different data structure');
        }

        setCakes(data);

        if (onCategoriesChange) {
          // Case-insensitive dedup so "Premium" and "premium" collapse into one tab.
          // First casing encountered wins as the display label.
          const categoryMap = new Map();
          for (const c of data) {
            if (c.category && !categoryMap.has(c.category.toLowerCase())) {
              categoryMap.set(c.category.toLowerCase(), c.category);
            }
          }
          const uniqueCategories = Array.from(categoryMap.values())
            .sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));
          onCategoriesChange(uniqueCategories);
        }
      } catch (error) {
        console.error('Error fetching cakes:', error);
        setCakes([]);
        if (onCategoriesChange) onCategoriesChange([]);
      }
    };

    fetchCakes();
  }, [onCategoriesChange]);

  const handleAddToCart = async (cake) => {
    if (isAddingToCart) return;
    setIsAddingToCart(true);
    addToCart(cake);
    setNotification(cake);
    setTimeout(() => {
      setNotification(null);
      setIsAddingToCart(false);
    }, 500);
  };

  const filteredCakes = selectedCategory === 'all'
    ? cakes
    : cakes.filter(cake =>
        cake.category && cake.category.toLowerCase() === selectedCategory.toLowerCase()
      );

  const displayCakes = limit ? filteredCakes.slice(0, limit) : filteredCakes;

  return (
    <div>
      {showAboutCard && (
        <div className="text-center space-y-3 mb-12">
          <h2 className="font-serif text-3xl md:text-4xl tracking-tight text-[#3D2817]">Our Collection</h2>
          <div className="flex items-center justify-center gap-3 text-[#6B7C3F]">
            <span className="h-px w-10 bg-[#6B7C3F]/40" />
            <Heart className="w-4 h-4 fill-current" />
            <span className="h-px w-10 bg-[#6B7C3F]/40" />
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-10 md:gap-12">
        {displayCakes.map((cake, index) => (
          <div key={index} className="flex flex-col group">
            <div className="relative aspect-square overflow-hidden bg-[#EFE7D6] mb-5">
              <img
                src={cake.image}
                alt={cake.name}
                className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-[1.03]"
                onError={(e) => {
                  e.target.src = '/api/placeholder/400/320';
                  e.target.alt = 'Image not available';
                }}
              />
            </div>
            <div className="space-y-2 text-center">
              <h3 className="font-serif text-xl text-[#3D2817]">{cake.name}</h3>
              {cake.description && (
                <p className="text-sm text-[#6B5644] leading-relaxed line-clamp-2">{cake.description}</p>
              )}
              <p className="text-[#3D2817] font-medium pt-1">${cake.price}</p>
              <button
                onClick={() => handleAddToCart(cake)}
                disabled={isAddingToCart}
                className="mt-3 inline-flex items-center justify-center gap-2 border border-[#3D2817] text-[#3D2817] hover:bg-[#3D2817] hover:text-[#FAF6EE] transition-colors px-6 py-2.5 tracking-[0.15em] uppercase text-xs disabled:opacity-50"
              >
                <ShoppingCart className="w-4 h-4" />
                {isAddingToCart ? 'Adding…' : 'Add to cart'}
              </button>
            </div>
          </div>
        ))}

        {showAboutCard && displayCakes.length % 3 === 2 && (
          <div className="aspect-square bg-[#6B7C3F] text-[#FAF6EE] p-8 flex flex-col justify-between">
            <div>
              <h3 className="font-serif text-3xl mb-3">Still hungry?</h3>
              <p className="text-sm opacity-90 leading-relaxed">
                Browse the full collection — there's something for every occasion.
              </p>
            </div>
            <Link
              to="/cakes"
              className="inline-block self-start border border-[#FAF6EE] px-6 py-2.5 tracking-[0.15em] uppercase text-xs hover:bg-[#FAF6EE] hover:text-[#6B7C3F] transition-colors"
            >
              View all cakes
            </Link>
          </div>
        )}
      </div>

      {notification && (
        <div className="fixed bottom-6 right-6 bg-[#3D2817] text-[#FAF6EE] px-5 py-4 shadow-lg z-50 flex items-center gap-3">
          <ShoppingCart className="w-5 h-5" />
          <p className="text-sm">{notification.name} added to cart</p>
        </div>
      )}
    </div>
  );
};

export default CakeCollection;
